<script lang="ts">
    import Modal from "@/components/Modal.svelte";
    import { Button } from "@/components/ui/button";
    import FileUpload from "@/components/Inputs/FileUpload.svelte";
    import { toast } from "svelte-sonner";
    import { useForm, page } from "@inertiajs/svelte";
    import type { VehicleAssignment, Document } from "@/types/types";
    import PreviewDocument from "@/components/documents/PreviewDocument.svelte";
    import { untrack } from 'svelte';

    type Props = {
        show: boolean;
        vehicleAssignment: VehicleAssignment;
        onClose: () => void;
        onDocumentUploaded?: () => void;
        onDocumentChange?: (document: Document | null) => void;
    };

    let { show = $bindable(), vehicleAssignment, onClose, onDocumentUploaded, onDocumentChange }: Props = $props();

    // Check if this is a new (unsaved) vehicle assignment
    // New assignments use the job's ID as a placeholder until saved
    let isNewAssignment = $derived(vehicleAssignment.id === vehicleAssignment.job?.id);

    // Local document state - starts with existing document from vehicleAssignment
    let localDocument = $state<Document | null>(findRamsDocument(vehicleAssignment.documents));

    // Find RAMS document from documents array
    function findRamsDocument(documents: Document[] | undefined): Document | null {
        if (!documents || documents.length === 0) {
            return null;
        }
        // First try to find a document with "RAMS" in the title
        const ramsDoc = documents.find(
            (doc) => doc.title?.toLowerCase()?.includes('rams')
        );
        if (ramsDoc) return ramsDoc;
        return null;
    }

    // Watch for flash data to update document after upload/delete
    $effect(() => {
        const flash = $page.props.flash as any;
        if (!flash) return;

        untrack(() => {
            // Handle new document from flash
            if (flash.document && flash.document.related_record_id === vehicleAssignment.id) {
                localDocument = flash.document;
            }

            // Handle deleted document
            if (flash.deletedId) {
                const currentDocId = localDocument?.id;
                if (currentDocId === flash.deletedId) {
                    localDocument = null;
                    // Notify parent that document was deleted
                    onDocumentChange?.(null);
                    // Trigger refetch so table icon updates
                    onDocumentUploaded?.();
                }
            }
        });
    });

    // Reset local document when vehicleAssignment changes
    $effect(() => {
        localDocument = findRamsDocument(vehicleAssignment.documents);
    });

    // Get job type for document title - job_type is directly on vehicleAssignment
    let jobType = $derived(vehicleAssignment.job_type || 'Job');
    let jobPostcode = $derived(vehicleAssignment.job?.address?.postcode || 'Unknown')
    let jobCustomerName = $derived(vehicleAssignment.job?.primaryCustomer?.name || vehicleAssignment.job?.customers[0]?.name || 'Unknown')
    let documentTitle = $derived(`RAMS - ${jobType} - ${jobPostcode} - ${jobCustomerName}`);
    let documentFilename = $derived(`RAMS_${jobType.replace(/\s+/g, '_')}_${jobPostcode.replace(/\s+/g, '_')}__${jobCustomerName.replace(/\s+/g, '_')}`);

    let createForm = $state(useForm({
        title: '',
        filename: '',
        file: null as File | null,
        related_record_type: 'App\\Models\\VehicleAssignment',
        related_record_id: vehicleAssignment.id,
        signature_required: true,
        status: 'active',
        _redirect_url: window.location.href
    }));

    // Update form values reactively when job type changes
    $effect(() => {
        $createForm.title = documentTitle;
        $createForm.filename = documentFilename;
    });

    function close() {
        show = false;
        onClose();
    }

    function submit(e: Event) {
        e.preventDefault();
        $createForm.processing = true;
        $createForm.post(route('documents.store' as any), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (page) => {
                // Get the uploaded document from flash data and set it immediately
                const flash = (page as any).props?.flash;
                if (flash?.document) {
                    localDocument = flash.document;
                    // Notify parent of the new document so it can update its state
                    onDocumentChange?.(flash.document);
                }
                $createForm.reset();
                toast.success("RAMS document uploaded successfully!");
                onDocumentUploaded?.();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error uploading RAMS document. " + message as string);
                }
            }
        });
    }

    // Get assignment label for display
    let assignmentLabel = $derived.by(() => {
        const vehicle = vehicleAssignment.vehicle;
        const job = vehicleAssignment.job;
        if (vehicle && job) {
            return `${vehicleAssignment.job.address.postcode} - ${vehicleAssignment.job_type || vehicleAssignment.title  || 'Job' }`;
        }
        if (vehicle) {
            return vehicle.registration;
        }
        return 'Job Assignment';
    });
</script>

{#if show}
    <Modal title="RAMS Document - {assignmentLabel}" onClose={close}>
        {#if isNewAssignment}
            <!-- Show message for unsaved assignments -->
            <div class="space-y-4">
                <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
                    <p class="text-sm text-amber-800 dark:text-amber-200">
                        This vehicle assignment hasn't been saved yet. Please save the scheduler first before uploading a RAMS document.
                    </p>
                </div>
                <div class="flex justify-end">
                    <Button variant="outline" onclick={close}>Close</Button>
                </div>
            </div>
        {:else if localDocument}
            <!-- Show existing document using PreviewDocument -->
            {#key localDocument.id}
                <PreviewDocument document={localDocument} />
            {/key}
            <div class="flex justify-end">
                <Button variant="outline" onclick={close}>Close</Button>
            </div>
        {:else}
            <!-- Upload form -->
            <form onsubmit={submit} class="space-y-4">
                <p class="text-muted-foreground">
                    Upload a RAMS (Risk Assessment Method Statement) document for this vehicle assignment.
                </p>

                <FileUpload
                    name="file"
                    server={null}
                    allowMultiple={false}
                    bind:value={$createForm['file']}
                    label="RAMS Document"
                    acceptedFileTypes={['application/pdf', 'image/*']}
                    maxFileSize="30MB"
                />

                {#if $createForm.errors.file}
                    <p class="text-sm text-destructive">{$createForm.errors.file}</p>
                {/if}

                <div class="flex justify-end gap-2">
                    <Button type="button" variant="outline" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing || !$createForm.file}>
                        {#if $createForm.processing}Uploading...{:else}Upload RAMS{/if}
                    </Button>
                </div>
            </form>
        {/if}
    </Modal>
{/if}
